import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Zap, Webhook, Key, BarChart2, Shield, Code2, ChevronRight,
  Database, ArrowRight, Globe, Layers, Sparkles, Building2, ScanLine,
  FileSearch, GitBranch, ScrollText, TrendingUp, Clock, Users, Check,
} from "lucide-react";
import { useAppConfig } from "@/contexts/AppConfigContext";

/* ── Animated counter ────────────────────────────────────────────────────── */
function Counter({ end, suffix = "", duration = 1800 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const step = Math.ceil(end / (duration / 16));
      const timer = setInterval(() => {
        start = Math.min(start + step, end);
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, 16);
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ── Flip Feature Card ───────────────────────────────────────────────────── */
const FLIP_STYLE = {
  perspective: "1000px",
};
const INNER_BASE = {
  transformStyle: "preserve-3d",
  transition: "transform 0.55s cubic-bezier(0.4,0.2,0.2,1)",
  position: "relative",
  width: "100%",
  height: "100%",
};
const FACE_BASE = {
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  position: "absolute",
  inset: 0,
  borderRadius: "16px",
  padding: "28px",
  display: "flex",
  flexDirection: "column",
};

function FlipCard({ icon: Icon, title, desc, quip, color = "#006080", dark = false }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      style={{ ...FLIP_STYLE, height: "210px", cursor: "pointer" }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      onClick={() => setFlipped(f => !f)}
      data-testid={`feature-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div style={{ ...INNER_BASE, transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}>
        {/* Front */}
        <div style={{
          ...FACE_BASE,
          background: dark ? "#0a1628" : "#ffffff",
          border: dark ? "1px solid #1e3a5f" : "1px solid #e2e8f0",
        }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: dark ? "rgba(0,96,128,0.3)" : "#E5F0F2", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <Icon size={20} color={dark ? "#4dc4e8" : color} />
          </div>
          <h3 style={{ fontWeight: 800, fontSize: 15, color: dark ? "#f1f5f9" : "#0f172a", marginBottom: 8 }}>{title}</h3>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: dark ? "#94a3b8" : "#64748b", flex: 1 }}>{desc}</p>
          <p style={{ fontSize: 11, color: dark ? "#4dc4e8" : color, fontWeight: 600, marginTop: 12 }}>Hover to flip →</p>
        </div>
        {/* Back */}
        <div style={{
          ...FACE_BASE,
          transform: "rotateY(180deg)",
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}>
          <Icon size={32} color="rgba(255,255,255,0.3)" style={{ marginBottom: 16 }} />
          <p style={{ fontSize: 16, fontWeight: 800, color: "#ffffff", lineHeight: 1.5, marginBottom: 12 }}>"{quip}"</p>
          <div style={{ width: 32, height: 2, background: "rgba(255,255,255,0.4)", borderRadius: 1, margin: "0 auto" }} />
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 12, fontWeight: 600 }}>{title}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Use-case tab data ───────────────────────────────────────────────────── */
const USE_CASES = [
  {
    domain: "Vehicle Dealerships",
    icon: "🚗",
    tagline: "Stop reading registration docs. Start selling cars.",
    pain: "Your staff manually re-types chassis numbers, plates, and prices from stacks of transfer papers and cumulative sale declarations. One typo = one very angry previous owner.",
    solution: "Build a pipeline with a Vehicle Schema that matches sale declarations, transfer papers, and registration cards. Extract plates, VINs, prices, buyer details, and notary references automatically via API.",
    steps: [
      "Upload PDFs via API or client link",
      "Pipeline identifies document type automatically",
      "Extracts all vehicle identifiers + pricing",
      "Webhook delivers to your DMS in real time",
    ],
    metrics: [
      { icon: TrendingUp, value: "97%", label: "Field accuracy" },
      { icon: Clock, value: "8×", label: "Faster processing" },
      { icon: Users, value: "0", label: "Manual re-entries" },
    ],
    accent: "#006080",
  },
  {
    domain: "Legal & Contracts",
    icon: "⚖️",
    tagline: "Due diligence without the due suffering.",
    pain: "Your paralegals spend 3 weeks reading 600 contracts looking for parties, dates, amounts, and termination clauses. By the time they're done, the deal is cold.",
    solution: "Schema identifiers match contract type by header keywords. Scoped extraction pulls parties, effective dates, liability caps, and governing law — without reading the boilerplate.",
    steps: [
      "Send contract batch via Bulk Processing",
      "Pipeline classifies NDAs vs. MSAs vs. Leases",
      "Schema extracts key parties and terms",
      "Export to CSV for review or fire webhook to DocuSign",
    ],
    metrics: [
      { icon: Clock, value: "10×", label: "Faster review" },
      { icon: TrendingUp, value: "94%", label: "Clause accuracy" },
      { icon: Users, value: "3 weeks", label: "→ 4 hours" },
    ],
    accent: "#1e3a5f",
  },
  {
    domain: "Insurance Claims",
    icon: "🏥",
    tagline: "15 form formats. 1 schema pipeline. Zero excuses.",
    pain: "Claims arrive in 15 different formats from 12 different brokers. Someone has to open every single one and manually enter policy numbers, loss amounts, and dates into your system.",
    solution: "Pipelines run all claim schemas in parallel. Wildcard schemas catch unknown formats. Regex post-processing normalises dates and amounts. Results fire to your claims management system via webhook.",
    steps: [
      "Broker sends PDF to your document request link",
      "Pipeline runs all claim schema patterns in parallel",
      "Matched template delivers normalised fields",
      "Webhook updates claim record instantly",
    ],
    metrics: [
      { icon: TrendingUp, value: "60%", label: "Less manual entry" },
      { icon: Clock, value: "< 10s", label: "Per claim" },
      { icon: Users, value: "12", label: "Broker formats → 1 output" },
    ],
    accent: "#7c3aed",
  },
  {
    domain: "Real Estate",
    icon: "🏠",
    tagline: "Extract the address. Not your will to live.",
    pain: "Deeds, lease agreements, appraisal reports, and title searches all look completely different. Agents copy-paste property addresses and parcel numbers into your CRM all day long.",
    solution: "Each document type gets its own schema with identifier patterns. Pipelines route deeds, leases, and appraisals to the right schema. Webhooks update your CRM the same day a document lands.",
    steps: [
      "Upload via agent portal or document request link",
      "Schema identifier matches document type",
      "Extract address, parties, amounts, and dates",
      "CRM updated via webhook within seconds",
    ],
    metrics: [
      { icon: Clock, value: "Same day", label: "Processing" },
      { icon: TrendingUp, value: "99%", label: "Address accuracy" },
      { icon: Users, value: "40%", label: "Agent time saved" },
    ],
    accent: "#059669",
  },
  {
    domain: "Logistics & Freight",
    icon: "📦",
    tagline: "Customs paperwork: the one thing slower than your shipment.",
    pain: "Your ops team processes waybills, bills of lading, customs declarations, and packing lists from 20+ carriers — all in different formats, all by hand, all before the container gets cold.",
    solution: "Build schemas for each carrier format with regex-based field extraction. Pipelines run in parallel on bulk uploads. Export to CSV or fire structured JSON to your TMS.",
    steps: [
      "Upload carrier docs via bulk processing API",
      "Pipeline classifies waybill vs BOL vs customs",
      "Extract shipment ID, weight, origin, destination",
      "TMS updated via webhook before arrival",
    ],
    metrics: [
      { icon: TrendingUp, value: "99.2%", label: "Field accuracy" },
      { icon: Clock, value: "5×", label: "Faster clearance" },
      { icon: Users, value: "20+", label: "Carrier formats handled" },
    ],
    accent: "#b45309",
  },
  {
    domain: "HR & Compliance",
    icon: "📋",
    tagline: "Audit trail? We've got a paper trail. Actually, we've got neither.",
    pain: "Onboarding documents, ID verifications, certifications, and compliance forms pile up across email threads and shared drives. Your HR manager's inbox is a compliance liability.",
    solution: "Send employees a document request link. They upload IDs and forms. Schemas extract names, dates, certificate numbers. Bulk delete + CSV export keeps your records clean and auditable.",
    steps: [
      "Employee receives secure document request link",
      "Uploads ID and compliance documents",
      "Schema extracts verified fields automatically",
      "Bulk export to CSV for compliance reporting",
    ],
    metrics: [
      { icon: Clock, value: "2 hrs", label: "→ 15 min onboarding" },
      { icon: TrendingUp, value: "100%", label: "Audit trail coverage" },
      { icon: Users, value: "0", label: "Lost documents" },
    ],
    accent: "#dc2626",
  },
];

/* ── Step card ───────────────────────────────────────────────────────────── */
function StepCard({ num, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#006080] text-white text-sm font-bold flex items-center justify-center">{num}</div>
      <div>
        <h4 className="font-semibold text-slate-900 text-sm mb-1">{title}</h4>
        <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/* ── Use Case Panel ──────────────────────────────────────────────────────── */
function UseCasePanel({ uc }) {
  return (
    <div className="space-y-6" data-testid={`usecase-panel-${uc.domain.replace(/\s+/g, "-").toLowerCase()}`}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">{uc.icon}</span>
          <h3 className="text-xl font-black text-slate-900">{uc.domain}</h3>
        </div>
        <p className="text-sm font-semibold text-[#006080] italic">"{uc.tagline}"</p>
      </div>

      {/* Pain point */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <span className="text-base mt-0.5">😩</span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-red-600 mb-1">The Pain</p>
            <p className="text-sm text-red-800 leading-relaxed">{uc.pain}</p>
          </div>
        </div>
      </div>

      {/* Solution */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <span className="text-base mt-0.5">✅</span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-1">The Solution</p>
            <p className="text-sm text-emerald-900 leading-relaxed">{uc.solution}</p>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">How it works</p>
        <div className="space-y-2">
          {uc.steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white mt-0.5" style={{ background: uc.accent }}>{i + 1}</div>
              <p className="text-sm text-slate-700">{s}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3">
        {uc.metrics.map(({ icon: Icon, value, label }) => (
          <div key={label} className="text-center p-3 bg-white border border-slate-200 rounded-xl">
            <Icon size={16} className="mx-auto mb-1" style={{ color: uc.accent }} />
            <p className="text-lg font-black" style={{ color: uc.accent }}>{value}</p>
            <p className="text-[11px] text-slate-500 font-medium leading-tight">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const { appName } = useAppConfig();
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const cardsScrollRef = useRef(null);

  const handleCardsScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    setShowScrollArrow(!atBottom);
  };

  const features = [
    {
      icon: ScanLine, title: "Mistral OCR", dark: false,
      desc: "State-of-the-art OCR on PDFs and images with full markdown output, per-page accuracy, and table extraction.",
      quip: "It reads your scanned fax from 1997. No judgment. Mostly.",
    },
    {
      icon: Database, title: "Schema Builder", dark: false,
      desc: "Design extraction schemas with per-field regex, post-processing steps, scope boundaries, and enable/disable toggles.",
      quip: "Your intern took 3 weeks to build this manually. You took 3 minutes. Don't tell them.",
    },
    {
      icon: Sparkles, title: "AI Schema Generator", dark: true,
      desc: "Upload a document and let Mistral AI auto-generate a full schema — field names, types, regex, and all. Edit live.",
      quip: "AI looked at your document and said 'I got this.' It was right. Infuriatingly.",
    },
    {
      icon: GitBranch, title: "Pipeline Processing", dark: false,
      desc: "Group schemas into pipelines. Documents are matched, extracted, and routed — all schemas run in parallel.",
      quip: "OCR → Match → Extract → Webhook. Faster than your daily standup. Way more useful too.",
    },
    {
      icon: Layers, title: "Bulk Processing", dark: true,
      desc: "Upload 500 PDFs at once. Extract everything. Download as CSV. Fire webhooks for each. Call it a Thursday.",
      quip: "Got 500 PDFs? We have a CSV. Your compliance team will cry. Happy tears.",
    },
    {
      icon: Code2, title: "20+ Post-processing Steps", dark: false,
      desc: "Trim, case transform, format phone/date, strip HTML, extract emails — chain any combination per field.",
      quip: "Your PM wants ISO 8601. Your client sent 'the 3rd of next month'. We handle the divorce.",
    },
    {
      icon: Webhook, title: "Webhook Delivery", dark: true,
      desc: "Automatically fire structured JSON payloads to any endpoint the moment extraction completes.",
      quip: "Results in your system before you finish explaining what an API is. We timed it.",
    },
    {
      icon: Key, title: "Pipeline API Keys", dark: false,
      desc: "Generate, rotate, and revoke scoped API keys per pipeline. Full audit trail, zero key sprawl.",
      quip: "Like crypto wallets, but for documents. Less existential dread. Same level of rotation.",
    },
    {
      icon: Building2, title: "Multi-tenant", dark: false,
      desc: "Superadmin manages companies, users, schemas, and credits with full isolation between tenants.",
      quip: "Run 50 companies on one platform. Each thinks they're your only customer. They're right.",
    },
    {
      icon: BarChart2, title: "Usage Reports", dark: true,
      desc: "Track document volumes, extraction rates, credit consumption, and overages across all companies.",
      quip: "Finally know how many pages you OCR'd this month. Spoiler: it's way more than you thought.",
    },
    {
      icon: ScrollText, title: "Document Requests", dark: false,
      desc: "Send clients a tokenized link. They upload. You get structured data. No emails, no chasing.",
      quip: "Send a link. They upload. You get data. Nobody asks 'did you get my fax?' anymore.",
    },
    {
      icon: Shield, title: "Scope Identifiers", dark: false,
      desc: "Restrict extraction to text between start/end regex patterns. Surgically precise schema boundaries.",
      quip: "Only care about page 3, section 2? Same. We'll ignore the other 38 pages for you.",
    },
  ];

  return (
    <div className="min-h-screen font-['Bricolage_Grotesque',_'Inter',_sans-serif] bg-[#F8F8F5] text-slate-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800;900&display=swap');
        .use-case-tab { transition: all 0.2s ease; }
        .use-case-tab:hover { transform: translateX(4px); }
        .use-case-tab.active { transform: translateX(0); }
        .panel-fade { animation: panelFade 0.3s ease; }
        @keyframes panelFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .cards-scroll::-webkit-scrollbar { display: none; }
        .cards-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
        .scroll-arrow { animation: scrollBounce 1.4s ease-in-out infinite; }
        .scroll-arrow-wrapper {
          transition: opacity 0.4s ease, transform 0.4s ease;
        }
        .scroll-arrow-wrapper.hidden-arrow {
          opacity: 0;
          transform: translateY(4px);
          pointer-events: none;
        }
      `}</style>

      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-[#F8F8F5]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#006080] rounded-md flex items-center justify-center">
              <FileText size={14} className="text-white" />
            </div>
            <span className="font-bold text-slate-900">{appName}</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#use-cases" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors hidden md:block">Use Cases</a>
            <a href="#features" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors hidden md:block">Features</a>
            <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors">Sign in</Link>
            <Link to="/login" data-testid="landing-get-started"
              className="h-8 px-4 bg-[#006080] text-white text-sm font-semibold rounded-lg hover:bg-[#004B66] transition-colors flex items-center gap-1.5">
              Get Started <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-20 pb-24 px-6">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #d1d5db 1px, transparent 0)",
          backgroundSize: "32px 32px", opacity: 0.4,
        }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-[#006080]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E5F0F2] border border-[#006080]/20 text-[#006080] text-xs font-semibold mb-8">
            <Zap size={11} /> Universal Document Intelligence Platform
          </div>
          <h1 className="text-5xl sm:text-6xl font-black text-slate-900 leading-tight mb-6 tracking-tight">
            Extract. Structure.{" "}<span className="text-[#006080]">Automate.</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Turn any document into structured data — invoices, contracts, vehicle registrations, IDs, or custom forms — with OCR + AI extraction, regex precision, and instant webhook delivery.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/login" className="h-11 px-6 bg-[#006080] text-white font-bold rounded-xl hover:bg-[#004B66] transition-all hover:scale-105 flex items-center gap-2 shadow-lg shadow-[#006080]/20">
              Start extracting <ArrowRight size={16} />
            </Link>
            <a href="#features" className="h-11 px-6 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-white hover:border-slate-300 transition-colors flex items-center gap-2">
              See features
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <section className="py-12 border-y border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { end: 12, suffix: "", label: "Feature areas" },
            { end: 20, suffix: "+", label: "Post-processing steps" },
            { end: 6, suffix: "", label: "Industry use cases" },
            { end: 99, suffix: "%", label: "Extraction accuracy" },
          ].map(({ end, suffix, label }) => (
            <div key={label}>
              <p className="text-3xl font-black text-[#006080] mb-1"><Counter end={end} suffix={suffix} /></p>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Flip Feature Cards ────────────────────────────────────────────── */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span className="inline-block px-3 py-1 rounded-full bg-[#E5F0F2] border border-[#006080]/20 text-[#006080] text-xs font-bold uppercase tracking-widest mb-4">Features</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">
              Everything you need to go from PDF to data.
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              Hover the cards. Flip them. Read the truth we're not allowed to put in the brochure.
            </p>
          </div>

          {/* Invisible scroll container — shows exactly 3 rows (210px × 3 + 16px gap × 2 = 662px) */}
          <div className="relative mt-12">
            <div
              ref={cardsScrollRef}
              className="cards-scroll overflow-y-auto"
              style={{ height: "662px" }}
              onScroll={handleCardsScroll}
            >
              <div className="grid grid-cols-2 gap-4">
                {features.map((f) => (
                  <FlipCard key={f.title} {...f} />
                ))}
              </div>
            </div>
            {/* Bottom fade hint — signals scrollable content below */}
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 rounded-b-lg"
              style={{ background: "linear-gradient(to bottom, transparent, #F8F8F5 90%)" }}
            />
            {/* Animated scroll indicator */}
            <div
              className={`scroll-arrow-wrapper absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10${showScrollArrow ? "" : " hidden-arrow"}`}
              onClick={() => cardsScrollRef.current?.scrollBy({ top: 242, behavior: "smooth" })}
              style={{ cursor: "pointer" }}
            >
              <span className="text-[10px] font-semibold tracking-widest uppercase text-[#006080]/70">More</span>
              <div className="scroll-arrow w-7 h-7 rounded-full bg-white border border-[#006080]/20 shadow-md flex items-center justify-center">
                <ChevronRight size={14} className="text-[#006080] rotate-90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Use Case Vertical Tabs ───────────────────────────────────────── */}
      <section id="use-cases" className="py-20 px-6 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-[#E5F0F2] border border-[#006080]/20 text-[#006080] text-xs font-bold uppercase tracking-widest mb-4">Use Cases</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">
              Your industry. Your documents. Our problem now.
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              Select a domain to see how teams actually use this — problem first, then the part where everything gets better.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: domain tabs */}
            <div className="lg:w-56 flex-shrink-0">
              <div className="space-y-1 lg:sticky lg:top-20">
                {USE_CASES.map((uc, i) => (
                  <button
                    key={uc.domain}
                    data-testid={`usecase-tab-${i}`}
                    onClick={() => setActiveUseCase(i)}
                    className={`use-case-tab w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all flex items-center gap-3 ${
                      activeUseCase === i
                        ? "active bg-[#006080] text-white shadow-md"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <span className="text-base">{uc.icon}</span>
                    <span className="leading-tight">{uc.domain}</span>
                    {activeUseCase === i && <ChevronRight size={14} className="ml-auto flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: use case panel */}
            <div className="flex-1 bg-[#F8F8F5] rounded-2xl border border-slate-200 p-6 lg:p-8">
              <div className="panel-fade" key={activeUseCase}>
                <UseCasePanel uc={USE_CASES[activeUseCase]} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-4">From upload to webhook in seconds</h2>
            <p className="text-slate-500 mb-8">Whether you're processing one document or thousands, {appName} handles the full pipeline — parallel schema matching included.</p>
            <div className="space-y-6">
              <StepCard num="1" title="Upload a document" desc="Via dashboard, API key, pipeline ingest, or a tokenized client link. PDF, JPEG, PNG, WebP." />
              <StepCard num="2" title="OCR + AI extraction" desc="Mistral OCR converts the document to text. Pipelines run all schemas in parallel — not serially." />
              <StepCard num="3" title="Regex + post-processing" desc="Per-field regex patterns override or supplement AI results. 20+ post-processing transforms available." />
              <StepCard num="4" title="Matched templates + webhook" desc="Results returned as matched_templates and other_templates. Webhooks fire to your endpoint instantly." />
            </div>
          </div>
          <div className="bg-slate-950 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-1.5 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-amber-500/80" />
              <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              <span className="text-slate-500 text-xs font-mono ml-2">POST /api/pipelines/:id/ingest</span>
            </div>
            <pre className="text-xs font-mono text-slate-300 leading-relaxed overflow-x-auto">{`// Request
{
  "filename": "sale_declaration.pdf",
  "mime_type": "application/pdf",
  "file_url": "https://cdn.acme.com/doc.pdf"
}

// Response (parallel evaluation):
{
  "matched_templates": [{
    "schema_name": "Vehicle Sale Schema",
    "matched": true,
    "llm_used": false,
    "extracted_fields": {
      "plate": "EL373DS",
      "chassis": "WBAKK310...",
      "price": "5,900.00 €"
    }
  }],
  "other_templates": [...]
}`}</pre>
          </div>
        </div>
      </section>

      {/* ── Dev + Business callout ───────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-8 rounded-2xl border border-[#006080]/20 bg-[#E5F0F2]/50">
            <Globe size={24} className="text-[#006080] mb-4" />
            <h3 className="text-xl font-black text-slate-900 mb-3">For developers</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {[
                "REST API with pipeline & schema key auth",
                "Base64 or URL document ingestion",
                "Regex + LLM extraction (toggleable per schema)",
                "Parallel pipeline evaluation — no serial waits",
                "Interactive API playground + pipeline tester",
              ].map(item => (
                <li key={item} className="flex items-center gap-2">
                  <Check size={13} className="text-[#006080] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-2xl bg-[#006080] text-white">
            <Shield size={24} className="text-white/80 mb-4" />
            <h3 className="text-xl font-black mb-3">For operators</h3>
            <ul className="space-y-2 text-sm text-white/80">
              {[
                "Multi-tenant company + user management",
                "Credit-based usage tracking with overage alerts",
                "Client document request links (dev & prod)",
                "Bulk processing with CSV export + bulk delete",
                "Superadmin dashboard + usage reports",
              ].map(item => (
                <li key={item} className="flex items-center gap-2">
                  <Check size={13} className="text-white/60 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-[#006080]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-4">Ready to stop typing what your scanner already knows?</h2>
          <p className="text-white/70 mb-8">Set up your first schema and start processing documents in minutes. Your team will think you hired more people. You didn't.</p>
          <Link to="/login"
            className="inline-flex items-center gap-2 h-12 px-8 bg-white text-[#006080] font-bold rounded-xl hover:bg-slate-100 transition-colors text-sm shadow-lg">
            Get started <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="py-8 px-6 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#006080] rounded-md flex items-center justify-center">
              <FileText size={12} className="text-white" />
            </div>
            <span className="text-sm font-bold text-slate-900">{appName}</span>
          </div>
          <p className="text-xs text-slate-400">Universal Document Intelligence Platform</p>
          <Link to="/login" className="text-xs text-[#006080] font-medium hover:underline">Sign in →</Link>
        </div>
      </footer>
    </div>
  );
}
